// src/components/EmployeeSnapshotSection.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Card,
  CardContent,
  Avatar,
  Divider,
} from "@mui/material";
import Grid from '@mui/material/Grid';
import {
  Work as WorkIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon,
  MailOutline as MailOutlineIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { doc, getDoc, collection, query, orderBy, onSnapshot, updateDoc, getDocs, where } from "firebase/firestore";
import { db, auth } from "../firebase";
import { useAvailability } from "../hooks/useAvailability";
import useEmployeeAssignments from "../hooks/useEmployeeAssignments";
// headshot/storage integration deferred by user

export default function EmployeeSnapshotSection({ employeeId }) {
  const [employee, setEmployee] = useState(null);
  // headshot state removed per user request
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load employee data
  useEffect(() => {
    if (!employeeId) return;

    const loadEmployee = async () => {
      setLoading(true);
      try {
        // Resolve tenantId and read tenant-scoped employee doc
        let tenantId = null;
        const adminRaw = localStorage.getItem('adminUser');
        if (adminRaw) {
          try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
        }
        if (!tenantId && auth && auth.currentUser) {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        }
        if (!tenantId) throw new Error('Tenant not found');
        const employeeRef = doc(db, `tenants/${tenantId}/employees`, employeeId);
        const employeeSnap = await getDoc(employeeRef);
        
        if (employeeSnap.exists()) {
          const data = { id: employeeSnap.id, ...employeeSnap.data() };
          setEmployee(data);
          // Auto-assign a 6-digit employeeId if missing
          if (!data.employeeId) {
            try {
              const assignUniqueId = async () => {
                const genCandidate = () => Math.floor(Math.random() * 900000) + 100000; // 100000-999999
                const maxAttempts = 6;
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                  const candidate = String(genCandidate());
                  // check uniqueness within tenant
                  const q = query(collection(db, `tenants/${tenantId}/employees`), where('employeeId', '==', candidate));
                  const snap = await getDocs(q);
                  if (snap.empty) {
                    const employeeRef = doc(db, `tenants/${tenantId}/employees`, employeeSnap.id);
                    await updateDoc(employeeRef, { employeeId: candidate });
                    // update local state
                    setEmployee(prev => ({ ...prev, employeeId: candidate }));
                    return candidate;
                  }
                }
                console.warn('Failed to assign unique employeeId after attempts');
                return null;
              };
              assignUniqueId().catch(e => console.warn('assign employeeId error', e));
            } catch (e) {
              console.warn('Could not auto-assign employeeId', e);
            }
          }
          // no headshot initialization — showing initials only
        }
      } catch (error) {
        console.error("Error loading employee:", error);
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
  }, [employeeId]);

  // headshot upload handlers removed per user request

  // Load locations, departments, and roles
  useEffect(() => {
    let unsubLoc = null;
    let unsubDept = null;
    let unsubRoles = null;
    let mounted = true;

    (async () => {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for snapshot listeners', e);
          return;
        }
      }
      if (!tenantId) return;

      try {
        const locQuery = query(collection(db, `tenants/${tenantId}/locations`), orderBy('name'));
        const deptQuery = query(collection(db, `tenants/${tenantId}/departments`), orderBy('name'));
        const roleQuery = query(collection(db, `tenants/${tenantId}/roles`), orderBy('name'));

        unsubLoc = onSnapshot(locQuery, snap => { if (!mounted) return; setLocations(snap.docs.map(d => ({ id: d.id, ...d.data() }))); }, err => { console.warn('locations listener error', err); setLocations([]); });
  unsubDept = onSnapshot(deptQuery, snap => { if (!mounted) return; setDepartments(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(dd => !dd.archived)); }, err => { console.warn('departments listener error', err); setDepartments([]); });
  unsubRoles = onSnapshot(roleQuery, snap => { if (!mounted) return; setRoles(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => !r.archived)); }, err => { console.warn('roles listener error', err); setRoles([]); });
      } catch (e) {
        console.warn('Failed to attach snapshot listeners', e);
      }
    })();

    return () => { mounted = false; try { if (typeof unsubLoc === 'function') unsubLoc(); } catch (e) {} try { if (typeof unsubDept === 'function') unsubDept(); } catch (e) {} try { if (typeof unsubRoles === 'function') unsubRoles(); } catch (e) {} };
  }, []);

  // Get availability data for summary
  const { availability } = useAvailability(null, employeeId);
  // Attendance scoring removed

  // Prefer flattened assignments (if present) for the snapshot view; fallback to nested on the employee doc
  const { nested: flattenedNested } = useEmployeeAssignments(employeeId);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Typography>Loading employee snapshot...</Typography>
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Employee not found.</Typography>
      </Box>
    );
  }

  // Helper functions
  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || "Unknown Location";
  };

  

  // Helper: derive assigned role ids and primary ids from assignments (prefer flattenedNested)
  // effectiveAssignments will prefer flattened docs reconstructed by the hook, then fallback to the employee doc
  const effectiveAssignments = (flattenedNested && Object.keys(flattenedNested).length) ? flattenedNested : (employee && employee.assignments ? employee.assignments : {});

  const getAssignedRoleIds = (assignments = effectiveAssignments) => {
  const assigned = new Set();
  const primary = new Set();
    Object.entries(assignments).forEach(([locationId, locationData]) => {
      Object.entries((locationData && locationData.departments) || {}).forEach(([deptId, deptData]) => {
        Object.entries((deptData && deptData.roles) || {}).forEach(([roleId, roleMeta]) => {
          if (!roleMeta) return;
          if (roleMeta.assigned) assigned.add(roleId);
          if (roleMeta.isPrimary) primary.add(roleId);
        });
      });
    });
    return { assignedIds: Array.from(assigned), primaryIds: Array.from(primary) };
  };

  const buildRoleObject = (role) => {
    const dept = departments.find(d => d.id === role.departmentId) || {};
    return {
      location: getLocationName(dept.locationId),
      department: dept.name || 'Unknown Department',
      role: role.name || 'Unknown Role'
    };
  };

  const getPrimaryRoles = () => {
  const { primaryIds } = getAssignedRoleIds(effectiveAssignments || {});
    if (!primaryIds.length) return [];
    return (roles || []).filter(r => primaryIds.includes(r.id)).map(buildRoleObject);
  };

  const getSecondaryRoles = () => {
  const { assignedIds, primaryIds } = getAssignedRoleIds(effectiveAssignments || {});
  const secondaryIds = assignedIds.filter(id => !primaryIds.includes(id));
    if (!secondaryIds.length) return [];
    return (roles || []).filter(r => secondaryIds.includes(r.id)).map(buildRoleObject);
  };

  const getAssignedLocations = () => {
    const assignments = effectiveAssignments || {};
    return Object.keys(assignments).map(locationId => getLocationName(locationId));
  };

  const getDefaultLocation = () => {
    const assignedLocations = getAssignedLocations();
    return assignedLocations[0] || "No location assigned";
  };

  const getAvailabilitySummary = () => {
    if (!Array.isArray(availability) || availability.length === 0) return ["No availability set"];

    // helper: map numeric day keys -> name (1=Monday ... 6=Saturday, 0=Sunday)
    const numericDayToName = (k) => {
      const map = { '1': 'monday', '2': 'tuesday', '3': 'wednesday', '4': 'thursday', '5': 'friday', '6': 'saturday', '0': 'sunday' };
      return map[String(k)] || String(k);
    };

    const entries = availability.slice().reverse(); // prefer later entries if duplicates
    const byDay = {}; // keyed by name: monday..sunday

    entries.forEach(e => {
      if (!e) return;

      // Case A: weeklySchedule style (EmployeeAvailabilityManager)
      if (e.weeklySchedule && typeof e.weeklySchedule === 'object') {
        Object.keys(e.weeklySchedule).forEach(key => {
          const name = numericDayToName(key.toString().toLowerCase());
          // last-wins so we set even if exist
          byDay[name] = { ...e.weeklySchedule[key], _source: 'weekly', _meta: { type: e.type } };
        });
        return;
      }

      // Case B: single-day availability documents (EmployeeAvailability.js)
      if (e.dayOfWeek) {
        const name = e.dayOfWeek.toString().toLowerCase();
        // normalize fields
        const obj = {
          isAvailable: e.isAvailable !== undefined ? e.isAvailable : (e.available !== undefined ? e.available : true),
          startTime: e.startTime || e.start || null,
          endTime: e.endTime || e.end || null,
          reason: e.notes || e.reason || e.notes || '',
          _source: 'single',
        };
        byDay[name] = obj;
        return;
      }
    });

    const daysOrder = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    const formatTime12Hour = (t) => {
      if (!t) return '';
      const m = String(t).match(/^(\d{1,2}):(\d{2})/);
      if (!m) return String(t);
      let hh = parseInt(m[1],10);
      const mm = m[2];
      const ampm = hh >= 12 ? 'PM' : 'AM';
      hh = hh % 12; if (hh === 0) hh = 12;
      return `${hh}:${mm} ${ampm}`;
    };

    const items = [];
    daysOrder.forEach(day => {
      const d = byDay[day];
      const label = day.charAt(0).toUpperCase() + day.slice(1);
      if (!d) return;

      const isAllDayAvailable = (d.type === 'all_day_available' || d.allDay || (d.isAvailable === true && !d.startTime && !d.endTime));
      if (isAllDayAvailable) return; // skip listing full-day available

      // Prepare item
      const item = { day: label, status: 'partial', text: '', reason: '' };

      if (d.isAvailable === false || d.type === 'all_day_not_available' || d.type === 'not_available') {
        item.status = 'unavailable';
        item.text = 'All day unavailable';
        item.reason = d.reason || '';
        items.push(item);
        return;
      }

      if (d.startTime && d.endTime) {
        // time-bounded availability -> treat as partial
        item.status = 'partial';
        item.text = `Available ${formatTime12Hour(d.startTime)} - ${formatTime12Hour(d.endTime)}`;
        item.reason = d.reason || '';
        items.push(item);
        return;
      }

      // fallback — mark as partial
      item.status = 'partial';
      item.text = 'Custom availability set';
      item.reason = d.reason || '';
      items.push(item);
    });

    return items.length ? items : [{ day: 'None', status: 'available', text: 'No availability adjustments' }];
  };

  const primaryRoles = getPrimaryRoles();
  const secondaryRoles = getSecondaryRoles();
  const availabilitySummary = getAvailabilitySummary();
  const assignedLocations = getAssignedLocations();
  const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unnamed employee';
  const initials = `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase() || '?';

  const getEmploymentStatusLabel = () => {
    const e = employee?.employment || {};
    const raw = (e.employmentType || e.type || e.status || '').toString().trim().toLowerCase();
    if (!raw) return 'Not set';
    if (raw.includes('full')) return 'Full-time';
    if (raw.includes('part')) return 'Part-time';
    // fall back to title-case-ish
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const availabilityItems = Array.isArray(availabilitySummary)
    ? availabilitySummary.filter((item) => typeof item === 'object' && item.day !== 'None')
    : [];
  const unavailableDaysCount = availabilityItems.filter((item) => item.status === 'unavailable').length;
  const partialAvailabilityCount = availabilityItems.filter((item) => item.status === 'partial').length;
  const hasAvailabilityAdjustments = availabilityItems.length > 0;
  const availabilityHeadline = hasAvailabilityAdjustments
    ? `${availabilityItems.length} custom day${availabilityItems.length === 1 ? '' : 's'}`
    : 'Open availability';
  const availabilitySubline = !hasAvailabilityAdjustments
    ? 'No availability restrictions on file'
    : unavailableDaysCount > 0 && partialAvailabilityCount > 0
      ? `${partialAvailabilityCount} available window${partialAvailabilityCount === 1 ? '' : 's'} and ${unavailableDaysCount} blackout day${unavailableDaysCount === 1 ? '' : 's'}`
      : partialAvailabilityCount > 0
        ? 'Times below show when this employee is available to work'
        : 'Days below are fully unavailable'
  ;
  const availabilityLegend = hasAvailabilityAdjustments
    ? [
        partialAvailabilityCount > 0 ? { label: 'Available window', color: 'info' } : null,
        unavailableDaysCount > 0 ? { label: 'Unavailable', color: 'error' } : null,
      ].filter(Boolean)
    : 'No availability restrictions on file';

  const formatBirthday = (value) => {
    if (!value) return 'Not provided';

    if (typeof value === 'string') {
      const parsed = new Date(`${value}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      }
      return value;
    }

    if (value?.toDate && typeof value.toDate === 'function') {
      const parsed = value.toDate();
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }

    return 'Not provided';
  };

  const summaryStats = [
    {
      label: 'Home Base',
      value: getDefaultLocation(),
      helper: assignedLocations.length > 1 ? `+${assignedLocations.length - 1} more location${assignedLocations.length - 1 === 1 ? '' : 's'}` : 'Primary scheduling location',
    },
    {
      label: 'Primary Role',
      value: primaryRoles[0]?.role || 'Not assigned',
      helper: primaryRoles.length > 1 ? `${primaryRoles.length} primary roles` : `${primaryRoles.length || 0} primary roles`,
    },
    {
      label: 'Availability',
      value: availabilityHeadline,
      helper: availabilitySubline,
    },
    {
      label: 'Birthday',
      value: formatBirthday(employee.dateOfBirth),
      helper: employee.dateOfBirth ? 'Birth date on file' : 'No birth date recorded',
    },
  ];

  const detailRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: 'text.secondary',
  };

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <Card
        elevation={0}
        sx={{
          mb: 3.5,
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 24px 50px rgba(17, 24, 39, 0.08)',
          background: 'linear-gradient(135deg, rgba(20,95,180,0.08) 0%, rgba(7,128,116,0.08) 100%)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 2.25, md: 3 },
            background: 'linear-gradient(135deg, rgba(21,101,192,0.92) 0%, rgba(9,80,149,0.88) 52%, rgba(16,124,115,0.82) 100%)',
            color: 'common.white',
          }}
        >
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} lg={7}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Avatar
                  sx={{
                    width: 86,
                    height: 86,
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: 'common.white',
                    border: '3px solid rgba(255,255,255,0.28)',
                    fontSize: '2rem',
                    fontWeight: 800,
                    boxShadow: '0 10px 26px rgba(0,0,0,0.18)',
                  }}
                >
                  {initials}
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: '1.9rem', md: '2.35rem' }, fontWeight: 800, lineHeight: 1.05, letterSpacing: -0.6 }}>
                    {fullName}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip
                      icon={<BadgeOutlinedIcon />}
                      label={`ID ${employee.employeeId || 'Assigning...'}`}
                      sx={{
                        color: 'common.white',
                        bgcolor: 'rgba(255,255,255,0.14)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        '& .MuiChip-icon': { color: 'common.white' },
                      }}
                    />
                    <Chip
                      label={getEmploymentStatusLabel()}
                      sx={{
                        color: 'common.white',
                        bgcolor: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.18)',
                      }}
                    />
                  </Stack>

                  <Stack spacing={1} sx={{ mt: 1.75 }}>
                    <Box sx={{ ...detailRowSx, color: 'rgba(255,255,255,0.88)' }}>
                      <MailOutlineIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body1" sx={{ color: 'inherit', fontWeight: 500 }}>
                        {employee.email || 'No email on file'}
                      </Typography>
                    </Box>
                    <Box sx={{ ...detailRowSx, color: 'rgba(255,255,255,0.88)' }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body1" sx={{ color: 'inherit', fontWeight: 500 }}>
                        {employee.phone || 'No phone number on file'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Grid container spacing={1.25}>
                {summaryStats.map((stat) => (
                  <Grid item xs={12} sm={6} key={stat.label}>
                    <Box
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        px: 1.75,
                        py: 1.5,
                        bgcolor: 'rgba(255,255,255,0.14)',
                        border: '1px solid rgba(255,255,255,0.18)',
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.9, color: 'rgba(255,255,255,0.72)' }}>
                        {stat.label}
                      </Typography>
                      <Typography sx={{ mt: 0.6, fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.2 }}>
                        {stat.value}
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: '0.78rem', color: 'rgba(255,255,255,0.76)' }}>
                        {stat.helper}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} xl={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(25,118,210,0.12)',
                    color: 'primary.main',
                  }}
                >
                  <WorkIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Scheduling Readiness
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Core assignment details for quick schedule decisions
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  mb: 2.25,
                  p: 1.75,
                  borderRadius: 3,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.7 }}>
                    Home Location
                  </Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {getDefaultLocation()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {assignedLocations.length > 1 ? `${assignedLocations.length} assigned locations total` : 'Single assigned location'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2.25 }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.7 }}>
                  Primary Roles
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                  {primaryRoles.length > 0 ? primaryRoles.map((role, idx) => (
                    <Chip
                      key={`${role.role}-${idx}`}
                      icon={<AutoAwesomeIcon />}
                      label={role.role}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                  )) : (
                    <Chip label="No primary role assigned" variant="outlined" />
                  )}
                </Stack>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2.25 }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.7 }}>
                  Other Roles
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                  {secondaryRoles.length > 0 ? secondaryRoles.map((role, idx) => (
                    <Chip
                      key={`${role.role}-${role.location}-${idx}`}
                      label={`${role.role}${role.location ? ` • ${role.location}` : ''}`}
                      size="small"
                      sx={{ bgcolor: 'grey.100', fontWeight: 600 }}
                    />
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No secondary roles assigned
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.7 }}>
                  All Locations
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                  {assignedLocations.length > 0 ? assignedLocations.map((location) => (
                    <Chip
                      key={location}
                      label={location}
                      size="small"
                      sx={{ bgcolor: 'rgba(25,118,210,0.08)', color: 'primary.dark', fontWeight: 700 }}
                    />
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No location assigned
                    </Typography>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} xl={4}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(237,108,2,0.12)',
                    color: 'warning.main',
                  }}
                >
                  <ScheduleIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Availability Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Weekly exceptions and scheduling constraints
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  mb: 2.25,
                  p: 1.75,
                  borderRadius: 3,
                  bgcolor: hasAvailabilityAdjustments ? 'rgba(25,118,210,0.08)' : 'rgba(46,125,50,0.08)',
                  border: '1px solid',
                  borderColor: hasAvailabilityAdjustments ? 'rgba(25,118,210,0.16)' : 'rgba(46,125,50,0.16)',
                }}
              >
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.7 }}>
                  Scheduling Signal
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800 }}>
                  {availabilityHeadline}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {availabilitySubline}
                </Typography>
                {Array.isArray(availabilityLegend) && availabilityLegend.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
                    {availabilityLegend.map((item) => (
                      <Chip
                        key={item.label}
                        label={item.label}
                        size="small"
                        color={item.color}
                        variant="outlined"
                        sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.65)' }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              {Array.isArray(availabilitySummary) && availabilitySummary.length > 0 ? (
                <Stack spacing={1.1}>
                  {availabilitySummary.map((it, idx) => {
                    if (typeof it === 'string') {
                      return (
                        <Box
                          key={idx}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {it}
                          </Typography>
                        </Box>
                      );
                    }

                    const statusTone = it.status === 'unavailable'
                      ? { bg: 'rgba(211,47,47,0.08)', border: 'rgba(211,47,47,0.18)', chip: 'error' }
                      : it.status === 'partial'
                        ? { bg: 'rgba(25,118,210,0.08)', border: 'rgba(25,118,210,0.18)', chip: 'info' }
                        : { bg: 'rgba(46,125,50,0.08)', border: 'rgba(46,125,50,0.18)', chip: 'success' };

                    return (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.4,
                          borderRadius: 3,
                          bgcolor: statusTone.bg,
                          border: '1px solid',
                          borderColor: statusTone.border,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Chip label={it.day} size="small" color={statusTone.chip} sx={{ fontWeight: 700 }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
                            {it.text}
                          </Typography>
                        </Stack>
                        {it.reason && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8 }}>
                            {it.reason}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography color="text.secondary">No availability information</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {employee.notes && (
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'rgba(94,53,177,0.1)',
                      color: 'rgb(94,53,177)',
                    }}
                  >
                    <NotesIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Key Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Important context for managers and schedulers
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {employee.notes}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
